// Production calendar, Shoot schedule tab: month and week on the Calendar cell (DESIGN rev 2.1 §2.6, §3.1), the day
// list, the day's hour grid, and the talent pool. Every interaction of the old views stays: drag a shoot onto another
// day, remove a shoot from its day, open its production, drop an available talent on a day, place shoots on
// half-hour slots by drag or by the arrows, reorder the day list.
import { useState, useEffect, useMemo } from 'react';
import { ArrowDown, ArrowUp, Check, ChevronLeft, ChevronRight, Clock, ExternalLink, TriangleAlert, Users, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import CalendarCell from '../../ui/CalendarCell';
import EmptyState from '../../ui/EmptyState';
import Icon from '../../ui/Icon';
import ListRow from '../../ui/ListRow';
import StageChip from '../../ui/StageChip';
import { useConfirm } from '../../ui/Confirm';
import { stageOf } from '../../ui/stages';
import { cx } from '../../ui/util';
import { clean } from '../../shell/copy';
import Modal from '../../ui/Modal';
import {
  DAY_NAMES, MONTH_NAMES, SCHEDULE_SLOTS, WEEK_DAY_NAMES, buildCalDays, canReorderShoots, cellClick, coverageReason, dateKey, fmtTime12,
  getWeekMonday, plural, prettyDate, recTitle, segmentInitialsList, segmentsByDate, talentName,
} from './util';

const MAX_CHIPS_MONTH = 3;
const MAX_CHIPS_WEEK  = 5;

// A day's items for a Calendar cell that unfolds in place: the first few, then a "+N more" control; unfolded, every
// item and a "Show less" control. The control rides along as the last item.
function foldItems(items, max, expanded) {
  if (items.length <= max) return items;
  return expanded ? [...items, { id: '__less', fold: 'less' }] : [...items.slice(0, max), { id: '__more', fold: 'more', n: items.length - max }];
}
export function FoldControl({ item, onExpand, onCollapse, total }) {
  return item.fold === 'more'
    ? <button type="button" className="ui-link ui-meta" title={`Show all ${total} in this day`} onClick={e => { e.stopPropagation(); onExpand(); }}>+{item.n} more</button>
    : <button type="button" className="ui-link ui-meta" title="Fold back to the first few" onClick={e => { e.stopPropagation(); onCollapse(); }}>Show less</button>;
}
export function useExpandedDays() {
  const [expanded, setExpanded] = useState(() => new Set());
  return {
    has: k => expanded.has(k),
    expand: k => setExpanded(prev => new Set(prev).add(k)),
    collapse: k => setExpanded(prev => { const n = new Set(prev); n.delete(k); return n; }),
  };
}

// One shoot (a segment on a day) as a draggable chip. Month view: a filmed segment is muted, marked and not draggable,
// and a broken booking names each reason; week view keeps its shorter reason, as before.
function ShootChip({ rec, segment, view, onOpen, onRemove }) {
  const title  = recTitle(rec);
  const label  = clean(segment.segmentLabel);
  const inits  = segmentInitialsList(segment).join(', ');
  const names  = (segment.talents || []).map(talentName).filter(Boolean).join(', ');
  const filmed = view === 'month' && segment.shootStatus === 'filmed';
  // Run 5 (D-060): a booked talent whose availability does not cover this day marks the chip, state based.
  const uncovered = (segment.talents || []).filter(t => t.covered === false);
  const reason = !uncovered.length ? ''
    : view === 'month'
      ? uncovered.map(t => coverageReason(talentName(t) || 'Talent', 'this day', t.coverage)).join('\n')
      : `${uncovered.map(talentName).filter(Boolean).join(', ')} not available this day`;
  const broken = Boolean(reason) && !filmed;
  return (
    <span className={cx('ui-chip', filmed && 'ui-chip--muted')} data-tone={broken ? 'danger' : undefined} draggable={!filmed}
      title={`${label}: ${title}${names ? ` (${names})` : ''}${filmed ? '\nFilmed' : ''}${reason ? `\n${reason}` : ''}`}
      onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('segment-id', segment.id); }}>
      <button type="button" className="ui-chip__main" onClick={e => { e.stopPropagation(); onOpen(rec); }}>
        {filmed && <Icon icon={Check} label="Filmed" />}
        {broken && <Icon icon={TriangleAlert} label="Talent not available" />}
        <span className="ui-chip__tag">{label}</span>
        <span className="ui-chip__title">{title}</span>
        {inits && <span className="ui-chip__tag">{inits}</span>}
      </button>
      <button type="button" className="ui-chip__x" aria-label={`Remove ${label} of ${title} from this date`} title="Remove from this date"
        onClick={e => { e.stopPropagation(); onRemove(rec, segment); }}>
        <Icon icon={X} />
      </button>
    </span>
  );
}

// The chip's remove deletes the segment (as before): ask first.
function useRemoveShoot(onUnscheduleSegment) {
  const confirm = useConfirm();
  return async (rec, segment) => {
    const ok = await confirm({
      title: 'Remove this shoot?',
      body: `Remove ${clean(segment.segmentLabel)} of ${recTitle(rec)} from the calendar. The segment is deleted from the production's shoot plan too.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (ok) onUnscheduleSegment(segment.id);
  };
}

function MonthNav({ label, onPrev, onNext, onToday, todayDisabled, unit = 'month' }) {
  return (
    <div className="ui-toolbar">
      <Button variant="quiet" iconOnly icon={ChevronLeft} label={`Previous ${unit}`} onClick={onPrev} />
      <span className="ui-heading" aria-live="polite">{label}</span>
      <Button variant="quiet" iconOnly icon={ChevronRight} label={`Next ${unit}`} onClick={onNext} />
      <Button variant="quiet" onClick={onToday} disabled={todayDisabled}>Today</Button>
    </div>
  );
}

// ── Month ─────────────────────────────────────────────────────────────────────
export function MonthView({ records, talentCounts = {}, canSelectDay, onMoveSegment, onUnscheduleSegment, onOpenVideoCard, onLoadCounts, onDaySelect, onAssignTalent, onDayDetailOpen }) {
  const now = new Date();
  const [year,     setYear]     = useState(now.getFullYear());
  const [month,    setMonth]    = useState(now.getMonth());
  const [dragOver, setDragOver] = useState(null);
  const folds = useExpandedDays();
  const removeShoot = useRemoveShoot(onUnscheduleSegment);
  const todayKey = dateKey(now);
  const byDate = useMemo(() => segmentsByDate(records), [records]);
  const days = useMemo(() => buildCalDays(year, month), [year, month]);

  useEffect(() => {
    if (!onLoadCounts) return;
    const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const last = new Date(year, month + 1, 0);
    onLoadCounts(from, `${year}-${String(month + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`);
  }, [year, month, onLoadCounts]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  return (
    <div className="ui-stack">
      <MonthNav label={`${MONTH_NAMES[month]} ${year}`} onPrev={prevMonth} onNext={nextMonth}
        onToday={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); }} />
      <div className="ui-month">
        {DAY_NAMES.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
        {days.map(day => {
          const key     = dateKey(day);
          const otherMo = day.getMonth() !== month;
          const events  = byDate[key] || [];
          const count   = talentCounts[key] || 0;
          const items   = foldItems(events.map(e => ({ ...e, id: e.segment.id })), MAX_CHIPS_MONTH, folds.has(key));
          const day0    = prettyDate(key);
          return (
            <CalendarCell key={key} date={key} outside={otherMo} today={key === todayKey} items={items} max={Infinity}
              aria-label={`${day0}${events.length ? `, ${plural(events.length, 'shoot')}` : ''}`}
              renderItem={it => (it.fold
                ? <FoldControl item={it} total={events.length} onExpand={() => folds.expand(key)} onCollapse={() => folds.collapse(key)} />
                : <ShootChip rec={it.rec} segment={it.segment} view="month" onOpen={onOpenVideoCard} onRemove={removeShoot} />)}
              action={(
                <span className="ui-cluster ui-cluster--nowrap">
                  {count > 0 && <span className="ui-meta ui-num" title={`${count} talent available`} aria-label={`${count} talent available`}>{count}</span>}
                  {canSelectDay && !otherMo && (
                    <Button variant="quiet" iconOnly icon={Users} label={`Show talent available on ${day0}`}
                      onClick={e => { e.stopPropagation(); onDaySelect?.(key); }} />
                  )}
                  {events.length > 0 && (
                    <Button variant="quiet" iconOnly icon={Clock} label={`Hourly schedule for ${day0}`}
                      onClick={e => { e.stopPropagation(); onDayDetailOpen?.(key); onDaySelect?.(key); }} />
                  )}
                </span>
              )}
              data-drop={dragOver === key || undefined}
              onClickCapture={cellClick(e => {
                // The day's number strip selects the day for the Talent pool and never opens the hour grid (as before)
                if (e.target.closest('.ui-cal__head')) { if (!otherMo) onDaySelect?.(key); return; }
                if (events.length > 0) onDayDetailOpen?.(key);
                onDaySelect?.(key);
              })}
              onDragOver={e => { e.preventDefault(); setDragOver(key); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => {
                e.preventDefault(); setDragOver(null);
                const segmentId = e.dataTransfer.getData('segment-id');
                if (segmentId) { onMoveSegment(segmentId, key); return; }
                const talentRaw = e.dataTransfer.getData('talent-chip');
                if (talentRaw && onAssignTalent && events.length >= 1) onAssignTalent(events[0].rec.id, JSON.parse(talentRaw));
              }} />
          );
        })}
      </div>
    </div>
  );
}

// ── Week ──────────────────────────────────────────────────────────────────────
export function WeekView({ records, onMoveSegment, onUnscheduleSegment, onOpenVideoCard }) {
  const now = new Date();
  const [baseDate, setBaseDate] = useState(() => getWeekMonday(now));
  const [dragOver, setDragOver] = useState(null);
  const folds = useExpandedDays();
  const removeShoot = useRemoveShoot(onUnscheduleSegment);
  const todayKey = dateKey(now);
  const byDate = useMemo(() => segmentsByDate(records), [records]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(baseDate); d.setDate(baseDate.getDate() + i); return d; }), [baseDate]);

  const shift = n => { const d = new Date(baseDate); d.setDate(d.getDate() + n); setBaseDate(d); };
  const [ws, we] = [weekDays[0], weekDays[6]];
  const mon = d => MONTH_NAMES[d.getMonth()].slice(0, 3);
  const weekLabel = ws.getMonth() === we.getMonth()
    ? `${mon(ws)} ${ws.getDate()} to ${we.getDate()}, ${we.getFullYear()}`
    : `${mon(ws)} ${ws.getDate()} to ${mon(we)} ${we.getDate()}, ${we.getFullYear()}`;

  return (
    <div className="ui-stack">
      <MonthNav unit="week" label={weekLabel} onPrev={() => shift(-7)} onNext={() => shift(7)} onToday={() => setBaseDate(getWeekMonday(now))} />
      <div className="ui-month ui-month--tall">
        {WEEK_DAY_NAMES.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
        {weekDays.map(day => {
          const key    = dateKey(day);
          const events = byDate[key] || [];
          const items  = foldItems(events.map(e => ({ ...e, id: e.segment.id })), MAX_CHIPS_WEEK, folds.has(key));
          return (
            <CalendarCell key={key} date={key} today={key === todayKey} items={items} max={Infinity}
              aria-label={`${prettyDate(key)}${events.length ? `, ${plural(events.length, 'shoot')}` : ''}`}
              renderItem={it => (it.fold
                ? <FoldControl item={it} total={events.length} onExpand={() => folds.expand(key)} onCollapse={() => folds.collapse(key)} />
                : <ShootChip rec={it.rec} segment={it.segment} view="week" onOpen={onOpenVideoCard} onRemove={removeShoot} />)}
              data-drop={dragOver === key || undefined}
              onDragOver={e => { e.preventDefault(); setDragOver(key); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => {
                e.preventDefault(); setDragOver(null);
                const segmentId = e.dataTransfer.getData('segment-id');
                if (segmentId) onMoveSegment(segmentId, key);
              }} />
          );
        })}
      </div>
    </div>
  );
}

// ── Day ───────────────────────────────────────────────────────────────────────
// Productions with a segment on the day, in shoot order; producers reorder with the arrows (POST /api/live-production/reorder).
export function DayView({ shootRecords, onOpenVideoCard, onReorder }) {
  const { user } = useAuth();
  const canReorder = canReorderShoots(user);
  const now = new Date();
  const todayKey = dateKey(now);
  const [viewDate, setViewDate] = useState(todayKey);
  const shift = n => { const [y, m, d] = viewDate.split('-').map(Number); setViewDate(dateKey(new Date(y, m - 1, d + n))); };

  const dayRecs = useMemo(() => {
    const filtered = shootRecords.filter(r => (r.shootSegments || []).some(s => s.shootDay?.slice(0, 10) === viewDate));
    return [...filtered].sort((a, b) => {
      if (a.shootOrder != null && b.shootOrder != null) return a.shootOrder - b.shootOrder;
      if (a.shootOrder != null) return -1;
      if (b.shootOrder != null) return 1;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  }, [shootRecords, viewDate]);

  const [localRecs, setLocalRecs] = useState(dayRecs);
  useEffect(() => { setLocalRecs(dayRecs); }, [dayRecs]);

  async function handleMove(recId, direction) {
    const idx = localRecs.findIndex(r => r.id === recId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= localRecs.length) return;
    const reordered = [...localRecs];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const newOrder = reordered.map((r, i) => ({ ...r, shootOrder: i + 1 }));
    setLocalRecs(newOrder);
    try { await onReorder(viewDate, newOrder.map(r => ({ id: r.id, shootOrder: r.shootOrder }))); }
    catch { setLocalRecs(dayRecs); }
  }

  return (
    <div className="ui-stack">
      <MonthNav unit="day" label={prettyDate(viewDate, true)} onPrev={() => shift(-1)} onNext={() => shift(1)}
        onToday={() => setViewDate(todayKey)} todayDisabled={viewDate === todayKey} />
      {localRecs.length === 0 ? (
        <div className="ui-region"><EmptyState message="No shoots on this day. Pick another day, or schedule from the Talent calendar." /></div>
      ) : (
        <div className="ui-region ui-list">
          {localRecs.map((rec, i) => {
            const title = recTitle(rec);
            const meta = [clean(rec.client?.name), clean(rec.shootLead?.name), clean(rec.talent?.name)].filter(Boolean).join(', ');
            return (
              <ListRow key={rec.id}
                leading={<><span className="ui-meta ui-num">#{i + 1}</span>{rec.shootTimeSlot && <span className="ui-meta ui-num">{fmtTime12(rec.shootTimeSlot)}</span>}</>}
                title={<span title={title}>{title}</span>} meta={meta || undefined}
                trailing={(
                  <>
                    <StageChip stage={stageOf({ cardStatus: rec.status })} />
                    {rec.status === 'Incomplete' && <Badge condition="Incomplete" />}
                    {canReorder && (
                      <>
                        <Button variant="quiet" iconOnly icon={ArrowUp} label={`Move ${title} up`} disabled={i === 0} onClick={() => handleMove(rec.id, 'up')} />
                        <Button variant="quiet" iconOnly icon={ArrowDown} label={`Move ${title} down`} disabled={i === localRecs.length - 1} onClick={() => handleMove(rec.id, 'down')} />
                      </>
                    )}
                    <Button variant="quiet" icon={ExternalLink} onClick={() => onOpenVideoCard(rec)}>View</Button>
                  </>
                )} />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── The day's hour grid (from a month cell) ───────────────────────────────────
// Operates on SEGMENTS: each row of the grid is one Cam with its own shoot time slot.
export function DayDetailDialog({ dateStr, entries, talentForDay, canReorder, onClose, onSegmentTimeSlot, onOpenVideoCard }) {
  const [localEntries, setLocalEntries] = useState(() => entries.map(e => ({ ...e, segment: { ...e.segment } })));
  const [dragOverSlot, setDragOverSlot] = useState(null);
  useEffect(() => { setLocalEntries(entries.map(e => ({ ...e, segment: { ...e.segment } }))); }, [entries]);

  const sorted = [...localEntries].sort((a, b) => {
    const ao = a.segment.sortOrder ?? 0;
    const bo = b.segment.sortOrder ?? 0;
    if (ao !== bo) return ao - bo;
    const at = a.segment.shootTimeSlot;
    const bt = b.segment.shootTimeSlot;
    if (at && bt) return at.localeCompare(bt);
    if (at) return -1;
    if (bt) return 1;
    return 0;
  });
  const placed       = sorted.filter(e => e.segment.shootTimeSlot);
  const unscheduledE = sorted.filter(e => !e.segment.shootTimeSlot);
  const slotMap = {};
  for (const e of placed) (slotMap[e.segment.shootTimeSlot] ||= []).push(e);
  const talentAvailMap = {};
  for (const av of (talentForDay || [])) talentAvailMap[av.talentId] = av;

  // Any segment talent not available in the chosen slot
  function getTalentConflict(segment) {
    const slot = segment.shootTimeSlot;
    for (const t of (segment.talents || [])) {
      if (!t.talentId) continue;
      const av = talentAvailMap[t.talentId];
      if (!av) return true;
      if (av.availabilityType === 'full_day') continue;
      if (slot && av.blockStart && av.blockEnd && (slot < av.blockStart || slot >= av.blockEnd)) return true;
    }
    return false;
  }

  async function setSlot(segmentId, timeSlot) {
    const prevSlot = localEntries.find(e => e.segment.id === segmentId)?.segment.shootTimeSlot ?? null;
    setLocalEntries(p => p.map(e => (e.segment.id === segmentId ? { ...e, segment: { ...e.segment, shootTimeSlot: timeSlot } } : e)));
    try { await onSegmentTimeSlot(segmentId, timeSlot); }
    catch { setLocalEntries(p => p.map(e => (e.segment.id === segmentId ? { ...e, segment: { ...e.segment, shootTimeSlot: prevSlot } } : e))); }
  }

  // The arrows move a shoot one half-hour slot, skipping slots that already hold another shoot; no-op at the edges.
  function handleMoveSlot(segmentId, direction) {
    const cur = localEntries.find(e => e.segment.id === segmentId)?.segment.shootTimeSlot;
    if (!cur) return;
    const step = direction === 'up' ? -1 : 1;
    let i = SCHEDULE_SLOTS.findIndex(s => s.value === cur);
    if (i === -1) return;
    for (i += step; i >= 0 && i < SCHEDULE_SLOTS.length; i += step) {
      const v = SCHEDULE_SLOTS[i].value;
      if (!localEntries.some(e => e.segment.id !== segmentId && e.segment.shootTimeSlot === v)) { setSlot(segmentId, v); return; }
    }
  }

  return (
    <Modal size="wide" title={prettyDate(dateStr)} meta={`${plural(localEntries.length, 'shoot')} scheduled. Drag a shoot onto a time.`} onClose={onClose}>
      <div className="ui-sidelist">
        <section className="ui-stack ui-stack--tight" aria-labelledby="dd-untimed">
          <div className="ui-section__head">
            <h3 className="ui-section__title" id="dd-untimed">No time set</h3>
            <span className="ui-meta">{unscheduledE.length}</span>
          </div>
          {unscheduledE.length === 0 && <p className="ui-p ui-meta">Every shoot has a time.</p>}
          {unscheduledE.map(({ segment, rec }) => (
            <div key={segment.id} className="ui-dragrow" draggable onDragStart={e => e.dataTransfer.setData('segment-id', segment.id)}>
              <span className="ui-dragrow__main">
                <span className="ui-meta">{clean(segment.segmentLabel)}</span>
                <span className="ui-truncate">{recTitle(rec)}</span>
                <span className="ui-cluster">
                  <StageChip stage={stageOf({ cardStatus: rec.status })} />
                  {rec.client?.initials && <span className="ui-meta">{rec.client.initials}</span>}
                </span>
              </span>
              <Button variant="quiet" iconOnly icon={ExternalLink} label={`Open ${recTitle(rec)}`} onClick={() => onOpenVideoCard(rec)} />
            </div>
          ))}
        </section>
        <div role="list" aria-label="Time slots">
          {SCHEDULE_SLOTS.map(slot => (
            <div key={slot.value} role="listitem" className={cx('ui-slot', slot.value.endsWith(':00') && 'ui-slot--hour')}
              data-drop={dragOverSlot === slot.value || undefined}
              onDragOver={e => { e.preventDefault(); setDragOverSlot(slot.value); }}
              onDragLeave={() => setDragOverSlot(null)}
              onDrop={e => { e.preventDefault(); setDragOverSlot(null); const id = e.dataTransfer.getData('segment-id'); if (id) setSlot(id, slot.value); }}>
              <span className="ui-slot__label ui-num">{slot.label}</span>
              <div className="ui-slot__body">
                {(slotMap[slot.value] || []).map(({ segment, rec }) => {
                  const conflict = getTalentConflict(segment);
                  const slotIdx  = SCHEDULE_SLOTS.findIndex(s => s.value === segment.shootTimeSlot);
                  const talentLine = (segment.talents || []).map(talentName).filter(Boolean).join(', ');
                  const title = recTitle(rec);
                  return (
                    <div key={segment.id} className="ui-dragrow" data-tone={conflict ? 'danger' : undefined} draggable
                      onDragStart={e => e.dataTransfer.setData('segment-id', segment.id)}>
                      <button type="button" className="ui-dragrow__main" title="Open the production" onClick={() => onOpenVideoCard(rec)}>
                        <span className="ui-meta">{clean(segment.segmentLabel)}</span>
                        <span className="ui-truncate">{title}</span>
                        <span className="ui-meta">{[rec.client?.initials, talentLine].filter(Boolean).join(', ')}</span>
                      </button>
                      {conflict && <Badge tone="danger">Talent conflict</Badge>}
                      {canReorder && (
                        <>
                          <Button variant="quiet" iconOnly icon={ArrowUp} label="Move up one half hour (skips taken slots)" disabled={slotIdx <= 0} onClick={() => handleMoveSlot(segment.id, 'up')} />
                          <Button variant="quiet" iconOnly icon={ArrowDown} label="Move down one half hour (skips taken slots)" disabled={slotIdx >= SCHEDULE_SLOTS.length - 1} onClick={() => handleMoveSlot(segment.id, 'down')} />
                        </>
                      )}
                      <Button variant="quiet" iconOnly icon={X} label={`Remove the time of ${title}`} onClick={() => setSlot(segment.id, null)} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ── Talent pool (under the month / week / day view) ───────────────────────────
// Select a day to colour every talent by availability; an available one can be dragged onto a month day to assign
// them to that day's first production; producers click a name to edit that talent's availability.
export function TalentPool({ selectedDay, talentLoading, talentForDay, allTalent, assignTalent, dragHint, onEditTalent }) {
  return (
    <section className="ui-section" aria-labelledby="pool-title">
      <div className="ui-section__head">
        <h2 className="ui-section__title" id="pool-title">Talent</h2>
        <p className="ui-section__note">
          {!selectedDay ? 'Select a date on the calendar to see availability.'
            : talentLoading ? 'Loading availability.'
              : `${prettyDate(selectedDay)}: ${talentForDay.length} of ${allTalent.length} available${dragHint ? '. Drag an available name onto the day to assign them.' : ''}`}
        </p>
      </div>
      {allTalent.length === 0 ? (
        <p className="ui-p ui-meta">No talent accounts yet.</p>
      ) : (
        <div className="ui-chip-row">
          {allTalent.map(t => {
            const noColor = !selectedDay || talentLoading;
            const avEntry = noColor ? null : talentForDay.find(av => av.talentId === t.id);
            const isAvail = Boolean(avEntry);
            const tone = noColor ? undefined : isAvail ? 'ok' : 'danger';
            const when = isAvail ? (avEntry.availabilityType === 'full_day' ? 'All day' : `${fmtTime12(avEntry.blockStart)} to ${fmtTime12(avEntry.blockEnd)}`) : '';
            const name = clean(t.name);
            return (
              <span key={t.id} className={cx('ui-chip', !isAvail && 'ui-chip--static')} data-tone={tone} draggable={isAvail}
                title={[name, when, !noColor && !isAvail ? 'not available' : ''].filter(Boolean).join(', ')}
                onDragStart={isAvail ? (e => { e.stopPropagation(); e.dataTransfer.setData('talent-chip', JSON.stringify({ ...avEntry, talentId: t.id, talentName: t.name })); }) : undefined}>
                {assignTalent ? (
                  <button type="button" className="ui-chip__main" aria-label={`Edit availability for ${name}`} onClick={() => onEditTalent({ id: t.id, name: t.name })}>
                    <span className="ui-chip__title">{name}</span>{when && <span className="ui-chip__tag">{when}</span>}
                  </button>
                ) : (
                  <span className="ui-chip__main"><span className="ui-chip__title">{name}</span>{when && <span className="ui-chip__tag">{when}</span>}</span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </section>
  );
}
